/*
 * Peel — the "Choose your case" picker.
 *
 * Three controls, all server-rendered by
 * sections/peel-case-picker.liquid:
 *
 *   the tabs   switch which case panel is shown. Every panel is already
 *              in the page, so this is a [hidden] toggle rather than a
 *              request, and the panel that opens slides in from the side
 *              of the row its tab sits on.
 *   the dots   switch which colourway a panel is showing. The photograph
 *              and the colour it is of are carried on the dot itself, the
 *              same way the collection card carries them.
 *   the phone  narrows a panel to one device: that phone's own
 *              photography, the colourways it is sold in, its own price,
 *              and a button through to that phone's case with the
 *              colourway already chosen. The phones, and what each one
 *              costs and sells, are rendered as JSON beside the dropdown
 *              by snippets/peel-product-devices.liquid; the photography
 *              is fetched from the product the first time a phone is
 *              looked at.
 *
 * The first two are not required for the section to work: the button is
 * an ordinary link through to the case line's collection, and a colourway
 * with no photograph is rendered as a plain dot rather than a button, so
 * every control on the page still does what it looks like it does with
 * this file missing. The dropdown is the exception and is drawn empty and
 * hidden — a phone list is only worth showing to a page that can act on
 * it, so this file is what fills it in and reveals it.
 *
 * The phone is the shopper's, not the case's: choosing one moves every
 * panel in the picker onto it, and it is remembered through
 * peelDeviceSelection, so a phone picked in the homepage device finder is
 * already chosen here and one picked here is still chosen on the
 * collection page after it.
 *
 * Delegated from the document rather than bound per section: the theme
 * editor replaces a section's markup on every settings change, which
 * would drop any listener sitting on the markup it replaced.
 */
(function () {
  function panelsOf(picker) {
    return picker.querySelectorAll('[data-case-panel]');
  }

  function tabsOf(picker) {
    return picker.querySelectorAll('[data-case-tab]');
  }

  /* ---- Tabs ---- */

  /* The panel that opens comes in from the side of the row its tab sits
     on: forward along the tabs and it enters from the right, back along
     them and from the left. Only the panel arriving is moved — two
     panels in motion means laying one over the other, and a card this
     tall would reflow the page under the shopper to do it. The stylesheet
     holds the distance, the timing and the answer for a shopper who has
     asked for less motion. */
  function slideIn(panel, forward) {
    panel.classList.remove('is-opening-next', 'is-opening-prev');
    /* Reading the layout back restarts the animation. Without it,
       pressing along the row faster than it plays re-adds a class the
       panel already carries, and nothing moves. */
    void panel.offsetWidth;
    panel.classList.add(forward ? 'is-opening-next' : 'is-opening-prev');
  }

  function showCase(picker, id, focusTab) {
    Array.prototype.forEach.call(tabsOf(picker), function (tab) {
      var on = tab.getAttribute('data-case-tab') === id;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      /* One stop in the tab row: arrow keys move between the tabs, Tab
         moves out of the row and into the panel. */
      tab.tabIndex = on ? 0 : -1;
      if (on && focusTab) tab.focus();
    });

    var panels = Array.prototype.slice.call(panelsOf(picker));
    var from = -1;
    var to = -1;
    panels.forEach(function (panel, at) {
      if (!panel.hidden) from = at;
      if (panel.getAttribute('data-case-panel') === id) to = at;
    });

    panels.forEach(function (panel, at) {
      var opening = panel.hidden && at === to;
      panel.hidden = at !== to;
      if (panel.hidden) return;
      /* Pressing the tab of the case already open is not an opening, and
         animating it would be a card twitching under a press that
         changed nothing. */
      if (opening) slideIn(panel, from === -1 || to > from);
      /* The case just opened is the one whose photography is worth
         fetching for the phone chosen — a hidden panel holds off. */
      showPhoto(panel);
    });
  }

  function moveTab(picker, current, step) {
    var tabs = Array.prototype.slice.call(tabsOf(picker));
    var at = tabs.indexOf(current);
    if (at === -1 || tabs.length < 2) return;
    var next = tabs[(at + step + tabs.length) % tabs.length];
    showCase(picker, next.getAttribute('data-case-tab'), true);
  }

  /* ---- Colourways ---- */

  /* A colourway with no photograph of its own keeps the picture already
     on screen: blanking the frame would be a worse answer than showing
     the case in the colour before it. */
  function setShot(img, shot) {
    if (!img || !shot || !shot.src) return;

    /* srcset first — the other way round the browser starts a fetch on
       the src and then picks all over again once the candidates land. */
    img.srcset = shot.srcset || '';
    img.src = shot.src;

    if (shot.alt) img.alt = shot.alt;

    /* The frame holds its shape from the image's own dimensions, so a
       colourway shot at a different size brings its own along or the
       panel jumps as it lands. */
    if (shot.width && shot.height) {
      img.setAttribute('width', shot.width);
      img.setAttribute('height', shot.height);
    }
  }

  function swatchShot(swatch) {
    return {
      src: swatch.getAttribute('data-swatch-src'),
      srcset: swatch.getAttribute('data-swatch-srcset'),
      width: swatch.getAttribute('data-swatch-width'),
      height: swatch.getAttribute('data-swatch-height'),
      alt: swatch.getAttribute('data-swatch-alt')
    };
  }

  /* The picture the panel should be showing: the case for the phone
     chosen, in the colourway chosen, and the line's own shot of that
     colourway until there is a phone or while its photography is still
     coming. */
  function showPhoto(panel) {
    var img = panel.querySelector('[data-case-image]');
    var swatch = activeSwatch(panel);
    if (!img || !swatch) return;
    setShot(img, deviceShot(panel, swatch) || swatchShot(swatch));
  }

  function showColor(panel, swatch) {
    var name = panel.querySelector('[data-case-color-name]');
    var color = swatch.getAttribute('data-swatch-color') || '';
    if (name) name.textContent = color;

    Array.prototype.forEach.call(panel.querySelectorAll('[data-case-swatch]'), function (dot) {
      var on = dot === swatch;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    /* Both read the colourway off the panel, so they follow the dots
       rather than being told: the picture is of the case in it, and the
       price and the link are for the variant it is. */
    showPhoto(panel);
    showBuy(panel);
  }

  /* ---- Phones ----
   *
   * A panel's phones are rendered beside its dropdown as JSON, one entry
   * per phone the line declares, carrying that phone's product, its
   * price and which colourway is which variant of it. Read once per
   * panel: the theme editor hands over fresh markup rather than mutating
   * this, so a stale parse is not a thing that can happen.
   */

  function devicesOf(panel) {
    if (panel.peelDevices) return panel.peelDevices;
    var node = panel.querySelector('[data-case-devices]');
    var list = [];
    if (node) {
      try {
        var parsed = JSON.parse(node.textContent);
        if (Object.prototype.toString.call(parsed) === '[object Array]') list = parsed;
      } catch (error) {
        /* A line whose JSON did not survive the page keeps its dropdown
           empty, which leaves the panel exactly as it renders. */
      }
    }
    panel.peelDevices = list;
    return list;
  }

  function deviceByKey(panel, key) {
    var devices = devicesOf(panel);
    for (var i = 0; i < devices.length; i++) {
      if (devices[i].key === key) return devices[i];
    }
    return null;
  }

  /* One entry per colourway: the first variant of it that can be bought,
     or the first of it when none can. A colour is split across two
     variants wherever a line carries a second option, and it is worth
     offering while either of them is in stock. */
  function colorsOf(device) {
    if (device.peelColors) return device.peelColors;
    var index = {};
    (device.variants || []).forEach(function (variant) {
      var key = String(variant.color || '').trim().toLowerCase();
      if (!key) return;
      var held = index[key];
      if (!held || (!held.available && variant.available)) index[key] = variant;
    });
    device.peelColors = index;
    return index;
  }

  function colorOf(swatch) {
    return String(swatch.getAttribute('data-swatch-color') || '').trim();
  }

  function activeSwatch(panel) {
    return panel.querySelector('[data-case-swatch].is-active');
  }

  /* The variant the button would buy: the colourway on screen, in the
     phone chosen, and only while it can actually be bought — a link to a
     sold-out variant is a worse landing than the product itself. */
  function chosenVariant(panel) {
    var device = panel.peelDevice;
    var active = activeSwatch(panel);
    if (!device || !active) return null;
    var variant = colorsOf(device)[colorOf(active).toLowerCase()];
    return variant && variant.available ? variant : null;
  }

  /* ---- The phone's own photography ----
   *
   * The panel is rendered with one shot per colourway, all of them off a
   * single product — every case in a line is the same case cut for a
   * different phone, and mapping media to colourways for each of them is
   * the expensive part. That is the right picture for a line, and the
   * wrong one once a shopper has said which phone they hold: the case
   * they are being shown is on somebody else's phone.
   *
   * So a chosen phone brings its own photography, fetched from Shopify's
   * product JSON the first time that phone is looked at rather than
   * rendered into the page. A shot for every colourway of every phone a
   * line is cut for is three hundred URLs a panel that draws twelve will
   * never show; one request, the moment a phone is chosen, is the same
   * bargain the product gallery strikes when it hydrates a colourway's
   * images on first look.
   *
   * Until it lands — and for a phone whose colourway carries no picture,
   * and wherever fetch is missing or the request fails — the panel keeps
   * the line's own shot of the colourway, which is the right colour and
   * the wrong phone rather than nothing.
   */

  function sizedUrl(src, width) {
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* Which of the product's options holds the colour, so a variant can be
     put to the dot that names it. Read off the payload rather than
     assumed to be the first: the same test peel-product-colorways makes
     of a product in Liquid. */
  function colorOptionIndex(product) {
    var options = (product && product.options) || [];
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      var name = String(option && option.name ? option.name : option).toLowerCase();
      if (name.indexOf('color') > -1 || name.indexOf('colour') > -1) return i;
    }
    return 0;
  }

  function shotsOf(product) {
    var shots = {};
    var at = colorOptionIndex(product);
    ((product && product.variants) || []).forEach(function (variant) {
      var image = variant.featured_image;
      var color = (variant.options || [])[at];
      var key = String(color || '').trim().toLowerCase();
      if (!key || !image || !image.src || shots[key]) return;
      shots[key] = {
        src: sizedUrl(image.src, 1400),
        srcset:
          sizedUrl(image.src, 700) + ' 700w, ' +
          sizedUrl(image.src, 1400) + ' 1400w, ' +
          sizedUrl(image.src, 2000) + ' 2000w',
        width: image.width,
        height: image.height
      };
    });
    return shots;
  }

  /* Asked for once per phone and kept on it. An empty answer is still an
     answer: a phone whose request failed is not asked about again on this
     page, it just keeps the line's photography. */
  function loadShots(panel, device) {
    if (device.peelShots || device.peelShotsAsked || !window.fetch || !device.url) return;
    device.peelShotsAsked = true;
    window
      .fetch(device.url + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (product) {
        device.peelShots = shotsOf(product);
        /* Read back off the panel rather than closed over: by the time
           this lands the shopper may be on another phone, another
           colourway, or another case entirely. */
        showPhoto(panel);
      })
      .catch(function () {
        device.peelShots = {};
      });
  }

  function deviceShot(panel, swatch) {
    var device = panel.peelDevice;
    if (!device) return null;
    /* Hidden panels wait: choosing a phone moves every case in the row
       onto it, and fetching photography for six cases to show one of them
       is five requests for pictures nobody has asked to see. The panel
       asks when its tab is opened. */
    if (!device.peelShots && !panel.hidden) loadShots(panel, device);
    var shot = device.peelShots ? device.peelShots[colorOf(swatch).toLowerCase()] : null;
    if (!shot) return null;
    return {
      src: shot.src,
      srcset: shot.srcset,
      width: shot.width,
      height: shot.height,
      alt: device.title ? device.title + ' — ' + colorOf(swatch) : swatch.getAttribute('data-swatch-alt')
    };
  }

  /* Newest phone first and grouped by brand, which peel-device-map.js
     already works out for the homepage and collection pickers — the
     three list the same phones in the same order, and this one is not
     the place to decide it again. Without that file the phones keep the
     order the collection handed them over in, deduped. */
  function deviceGroups(devices) {
    var map = window.peelDeviceMap;
    if (map && map.group) return map.group(devices);

    var unique = [];
    var byKey = {};
    devices.forEach(function (device) {
      if (!device || !device.key || byKey[device.key]) return;
      byKey[device.key] = device;
      unique.push(device);
    });
    return unique.length ? [{ name: '', devices: unique }] : [];
  }

  function fillDevices(panel) {
    var select = panel.querySelector('[data-case-device]');
    if (!select) return;
    var tile = panel.querySelector('[data-case-device-tile]');
    var count = 0;

    deviceGroups(devicesOf(panel)).forEach(function (group) {
      var parent = select;
      if (group.name) {
        parent = document.createElement('optgroup');
        parent.label = group.name;
        select.appendChild(parent);
      }
      group.devices.forEach(function (device) {
        var option = document.createElement('option');
        option.value = device.key;
        option.textContent = device.label || device.value || device.key;
        parent.appendChild(option);
        count += 1;
      });
    });

    /* Nothing declared to list — an empty dropdown helps nobody, the
       same call the homepage picker makes. */
    if (tile) tile.hidden = count === 0;
  }

  /* The dots. Which of them are in the row is the one question a phone
     answers here, and it answers it twice over.

     Without a phone the row is the line's, capped where the section
     rendered it: two dozen colourways unioned across the phones a line
     is cut for is more row than the panel has, and more choosing than a
     shopper who has not said which phone they hold is doing. The dots
     past the cap are in the page but out of the row, and the "+N" counts
     them.

     With a phone the row is that phone's whole palette — every colourway
     it can be bought in, and nothing else. The ones it is not sold in
     come out of the row rather than being drawn crossed through it: a
     shopper choosing a colour for the phone in their hand should not be
     reading past nine struck-out dots to find the three that are theirs.
     And the cap comes off with them, because one phone's palette is the
     size of a row: a colour of theirs held back behind a "+N" is a
     colour they would have to leave the panel to find.

     Nothing is thrown away either way. The line sells all of them, so
     clearing the phone puts its own capped row straight back. */
  function showColors(panel) {
    var device = panel.peelDevice;
    var colors = device ? colorsOf(device) : null;
    var firstInRow = null;
    var shown = 0;

    Array.prototype.forEach.call(panel.querySelectorAll('.peel-case-swatch'), function (dot) {
      var name = colorOf(dot);
      var variant = colors && name ? colors[name.toLowerCase()] : null;
      /* data-swatch-over marks a dot the section rendered past its cap.
         It is what the "+N" counts while the row is the line's, and it
         stops meaning anything the moment a phone is chosen. */
      var inRow = device
        ? !!(variant && variant.available)
        : !dot.hasAttribute('data-swatch-over');

      dot.hidden = !inRow;
      if (inRow) shown += 1;
      if (dot.tagName === 'BUTTON') {
        /* Disabled as well as hidden. [hidden] is what takes the dot out
           of the row, and the attribute is what keeps it out of the tab
           order and out of a click while the stylesheet that draws the
           row is still on its way. */
        dot.disabled = !inRow;
        if (inRow && !firstInRow) firstInRow = dot;
      }
      /* No phone to qualify it with any more: a dot that is drawn is one
         that can be bought, and one that cannot is not drawn. */
      if (name) dot.title = name;
    });

    /* The colourway on screen has fallen out of the row — the phone just
       chosen is not sold in it, or the phone just cleared put the line's
       cap back over it — so the panel moves to one that is in the row
       rather than leaving the shopper looking at a case the row no longer
       offers. A panel that never had a chosen dot to lose is left as it
       was rendered until a phone is chosen, which is the first thing to
       make a colourway the shopper's rather than the merchant's. */
    var active = activeSwatch(panel);
    if (firstInRow && (active ? active.hidden : !!device)) {
      showColor(panel, firstInRow);
      active = firstInRow;
    }

    /* The colour beside the label is whichever dot is chosen and still in
       the row. With a phone chosen and none of them left it says nothing
       — naming a colourway the row no longer offers is the one thing it
       must not do. Without a phone it falls back to what it was rendered
       as, which is what a panel whose colourways carry no photography —
       so no dot is ever chosen — shows the whole time. */
    var named = panel.querySelector('[data-case-color-name]');
    if (named) {
      if (active && !active.hidden) named.textContent = colorOf(active);
      else if (device) named.textContent = '';
      else named.textContent = named.getAttribute('data-case-color-base') || '';
    }

    /* A label over an empty row reads as something that failed to load.
       What is missing is said once, in the note under the dropdown. */
    var tile = panel.querySelector('[data-case-colors]');
    if (tile) tile.hidden = shown === 0;

    showMore(panel, shown);
    return shown;
  }

  /* The "+N" at the end of the row: the colourways held back from it.
     Rendered as the line's — the palette past the cap — and recounted
     against the phone once there is one, which normally comes to nothing
     and takes the count away, since choosing a phone lifts the cap and
     draws that phone's palette whole.

     Recounted rather than simply hidden, so that the one case where a
     phone does have a colour the row has not got — a colourway declared
     on its options that the line's own walk never saw — still says so
     and still points at the page that has it. */
  function showMore(panel, shown) {
    var more = panel.querySelector('[data-case-more]');
    if (!more) return;
    var device = panel.peelDevice;
    var over = parseInt(more.getAttribute('data-case-more-count'), 10) || 0;

    if (device) {
      over = (device.colorCount || 0) - shown;
      if (over < 0) over = 0;
    }
    more.textContent = '+' + over;
    more.hidden = over === 0;

    /* The colourways past the cap are on the phone's own page once a
       phone is chosen, rather than spread across the line. */
    if (more.tagName === 'A') {
      var target = more.getAttribute('data-case-more-base') || more.getAttribute('href');
      if (device && device.url) target = device.url;
      if (target) more.href = target;
    }
  }

  function deviceName(device) {
    return device.label || device.value || '';
  }

  /* The figure and the button. Both were rendered for the whole line, and
     both carry what they were rendered as, so clearing the phone puts the
     line back rather than leaving the panel on the last phone looked at. */
  function showBuy(panel) {
    var device = panel.peelDevice;
    var variant = chosenVariant(panel);
    var price = panel.querySelector('[data-case-price]');
    var cta = panel.querySelector('[data-case-cta]');

    /* A price typed into the block is the merchant's own wording, and a
       phone chosen on the storefront is not a reason to overwrite it. */
    if (price && !price.hasAttribute('data-case-price-fixed')) {
      if (!device) {
        price.innerHTML = price.getAttribute('data-case-price-base') || price.innerHTML;
      } else {
        var figure = (variant && variant.price) || device.price;
        if (figure) price.textContent = figure;
      }
    }
    if (cta) {
      var target = cta.getAttribute('data-case-cta-base') || cta.getAttribute('href');
      if (device && device.url) {
        target = variant ? device.url + '?variant=' + variant.id : device.url;
      }
      if (target) cta.href = target;
    }
  }

  function showNote(panel, shown) {
    var note = panel.querySelector('[data-case-device-note]');
    if (!note) return;
    var device = panel.peelDevice;
    var text = '';
    if (device && device.available === false) {
      text = 'Sold out for ' + deviceName(device) + '.';
    } else if (device && shown === 0) {
      text = deviceName(device) + ' is cut in other colors — see them on its page.';
    }
    note.textContent = text;
    note.hidden = text === '';
  }

  function showDevice(panel, key) {
    var select = panel.querySelector('[data-case-device]');
    var device = key ? deviceByKey(panel, key) : null;
    if (select) select.value = device ? device.key : '';
    panel.peelDevice = device;
    showNote(panel, showColors(panel));
    showPhoto(panel);
    showBuy(panel);
    return device;
  }

  /* One shopper, one phone: a phone chosen on one case is chosen on all
     of them, so switching tabs keeps it rather than asking again. A case
     line that is not cut for it goes back to the whole line. */
  function showDeviceEverywhere(key, remember) {
    var device = null;
    Array.prototype.forEach.call(document.querySelectorAll('[data-case-panel]'), function (panel) {
      if (!panel.querySelector('[data-case-device]')) return;
      device = showDevice(panel, key) || device;
    });
    if (remember && window.peelDeviceSelection) {
      window.peelDeviceSelection.set(
        device ? { brand: device.brand, model: deviceName(device), key: device.key } : null
      );
    }
  }

  /* Fills every dropdown that has not been filled yet and opens each on
     the phone the shopper has already told the site about. */
  function initDevices() {
    var started = false;
    Array.prototype.forEach.call(document.querySelectorAll('[data-case-panel]'), function (panel) {
      if (panel.peelDeviceReady || !panel.querySelector('[data-case-device]')) return;
      panel.peelDeviceReady = true;
      fillDevices(panel);
      started = true;
    });
    if (!started) return;

    var stored = window.peelDeviceSelection ? window.peelDeviceSelection.get() : null;
    showDeviceEverywhere(stored && stored.key ? stored.key : '', false);
  }

  /* ---- Wiring ---- */

  document.addEventListener('click', function (event) {
    var target = event.target.closest('[data-case-tab],[data-case-swatch]');
    if (!target) return;
    var picker = target.closest('[data-peel-case-picker]');
    if (!picker) return;

    if (target.hasAttribute('data-case-tab')) {
      showCase(picker, target.getAttribute('data-case-tab'), false);
      return;
    }

    var panel = target.closest('[data-case-panel]');
    if (panel) showColor(panel, target);
  });

  /* The class comes off once it has played, so the next opening is a
     fresh one and a panel at rest carries nothing. Cancelled counts as
     played: a panel hidden mid-slide — the row pressed again before it
     finished — never fires the end event. */
  function endSlide(event) {
    var panel = event.target;
    if (!panel.classList || !panel.hasAttribute('data-case-panel')) return;
    panel.classList.remove('is-opening-next', 'is-opening-prev');
  }
  document.addEventListener('animationend', endSlide);
  document.addEventListener('animationcancel', endSlide);

  document.addEventListener('change', function (event) {
    var select = event.target.closest ? event.target.closest('[data-case-device]') : null;
    if (!select || !select.closest('[data-peel-case-picker]')) return;
    showDeviceEverywhere(select.value, true);
  });

  /* Arrow keys move along the tab row, Home and End to either end — what
     a tablist is expected to do once it has taken the tab stops. */
  document.addEventListener('keydown', function (event) {
    var tab = event.target.closest && event.target.closest('[data-case-tab]');
    if (!tab) return;
    var picker = tab.closest('[data-peel-case-picker]');
    if (!picker) return;

    var handled = true;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        moveTab(picker, tab, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        moveTab(picker, tab, -1);
        break;
      case 'Home':
        showCase(picker, tabsOf(picker)[0].getAttribute('data-case-tab'), true);
        break;
      case 'End':
        var tabs = tabsOf(picker);
        showCase(picker, tabs[tabs.length - 1].getAttribute('data-case-tab'), true);
        break;
      default:
        handled = false;
    }
    if (handled) event.preventDefault();
  });

  /* Selecting a case block in the theme editor opens its tab, so a
     merchant editing the fourth case is looking at the fourth case
     rather than at the first one. */
  document.addEventListener('shopify:block:select', function (event) {
    var tab = event.target.closest ? event.target.closest('[data-case-tab]') : null;
    if (!tab) return;
    var picker = tab.closest('[data-peel-case-picker]');
    if (picker) showCase(picker, tab.getAttribute('data-case-tab'), false);
  });

  /* The theme editor hands over fresh markup for the whole section on
     every settings change, so its dropdowns are empty again and want
     filling again. */
  document.addEventListener('shopify:section:load', initDevices);

  /* Deferred, so the document is parsed by the time this runs — and so
     peel-device-map.js, deferred ahead of it, has already built the
     phone catalogue this orders its dropdowns by. */
  initDevices();
})();
